import { Icon } from '@zoom/components';
import { Kind as IconKind } from '@zoom/components/dist/Icon';
import classnames from 'classnames';
import React from 'react';

export type InfoboxMessageType = 'info' | 'warning' | 'error';

const ICON_TYPE_CONFIG: Record<string, { color: string; kind: IconKind }> = {
  info: { color: '#239ed8', kind: 'info' },
  warning: { color: '#e07337', kind: 'warning' },
  error: { color: '#fc5d45', kind: 'warning' },
};

export interface ComposedInfoboxProps extends React.PropsWithChildren {
  dataTestId?: string;
  additionalMessage?: React.ReactNode;
  additionalMessageType?: InfoboxMessageType;
  iconSize?: number;
  mainMessageType?: InfoboxMessageType;
  position?: 'before' | 'after';
}

export const createMainMessageContainerTestId = (id: string) => `${id}:main-message-container`;
export const createMainMessageIconTestId = (id: string) => `${id}:main-message-icon`;
export const createAdditionalMessageContainerTestId = (id: string) => `${id}:additional-message-container`;
export const createAdditionalMessageIconTestId = (id: string) => `${id}:additional-message-icon`;

export const ComposedInfobox: React.FC<ComposedInfoboxProps> = ({
  dataTestId,
  additionalMessage,
  children,
  iconSize = 14,
  position = 'before',
  additionalMessageType = 'warning',
  mainMessageType = 'info',
}) => (
  <div className="Infobox Infobox--composed" data-test-id={dataTestId}>
    {additionalMessage && (
      <div
        className={classnames('Infobox__container', {
          [`Infobox__container--${additionalMessageType}`]: additionalMessageType !== 'info',
          'Infobox__container--after': position === 'after',
        })}
        data-test-id={dataTestId && createAdditionalMessageContainerTestId(dataTestId)}
      >
        <div className="Infobox__icon">
          <Icon
            {...ICON_TYPE_CONFIG[additionalMessageType]}
            size={iconSize}
            data-test-id={dataTestId && createAdditionalMessageIconTestId(dataTestId)}
          />
        </div>
        <div className="Infobox__content">{additionalMessage}</div>
      </div>
    )}
    <div
      className={classnames('Infobox__container', {
        [`Infobox__container--${mainMessageType}`]: mainMessageType !== 'info',
        'Infobox__container--single': !additionalMessage,
      })}
      data-test-id={dataTestId && createMainMessageContainerTestId(dataTestId)}
    >
      <div className="Infobox__icon">
        <Icon
          {...ICON_TYPE_CONFIG[mainMessageType]}
          size={iconSize}
          data-test-id={dataTestId && createMainMessageIconTestId(dataTestId)}
        />
      </div>
      <div className="Infobox__content">{children}</div>
    </div>
  </div>
);
